import type { AgentView, MessageView, PersonView, ProjectRole } from "@walkie/protocol"
import { useSyncExternalStore } from "react"
import type { Me, Metrics, Room } from "../api/types"

export type SocketStatus = "connecting" | "live" | "reconnecting" | "offline"
export type Filter = "all" | "requests" | "blockers" | "mine" | "failing"

export interface SendState {
	status: "sending" | "failed"
	error: string | null
	/** The exact body to re-POST; same clientMsgId so a retry cannot double-post. */
	payload: Record<string, unknown> & { body: string; clientMsgId: string }
}

export interface UiState {
	roomId: string | null
	threadId: string | null
	selectedId: string | null
	leftOpen: boolean
	rightOpen: boolean
	palette: boolean
	shortcuts: boolean
	connect: boolean
	settings: boolean
	newProject: boolean
	userMenu: boolean
	filter: Filter
	authorFilter: string | null
	focusAgentId: string | null
	resolveFor: string | null
	/** Message awaiting withdraw confirmation. */
	withdrawFor: string | null
	/** A "Cite" from a message's actions, picked up by that composer. */
	citeAdd: { id: string; target: "main" | "thread"; tick: number } | null
	/** Bumped to ask the composer (main or thread) to take focus. */
	focusComposer: { target: "main" | "thread"; tick: number }
}

export interface State {
	me: Me | null
	meError: string | null
	projectId: string | null
	role: ProjectRole | null
	agents: Record<string, AgentView>
	people: PersonView[]
	rooms: Record<string, Room>
	messages: Record<string, MessageView>
	sending: Record<string, SendState>
	threadsLoaded: Record<string, true>
	metrics: Metrics | null
	loading: boolean
	loadError: string | null
	hasOlder: boolean
	/** Realtime status per project socket (one socket per project). */
	sockets: Record<string, SocketStatus>
	/** Bumped by every realtime frame from a project that is not open (the projects page refreshes on it). */
	pulse: number
	/** Unseen realtime activity in projects other than the open one. */
	activity: Record<string, number>
	/** Hide GitHub-authored messages in the conversation (per project, remembered). */
	hideGithub: boolean
	now: number
	toasts: Toast[]
	ui: UiState
}

export interface Toast {
	id: number
	text: string
	kind: "error" | "info"
}

const prefs = (() => {
	try {
		return JSON.parse(localStorage.getItem("walkie.ui") ?? "{}") as Partial<{
			leftOpen: boolean
			rightOpen: boolean
			projectId: string
			rooms: Record<string, string>
		}>
	} catch {
		return {}
	}
})()

export function savePrefs(p: Partial<typeof prefs>) {
	Object.assign(prefs, p)
	try {
		localStorage.setItem("walkie.ui", JSON.stringify(prefs))
	} catch {
		/* private mode */
	}
}
export const loadPrefs = () => prefs

const githubKey = (pid: string) => `walkie.hideGithub.${pid}`
export function loadHideGithub(pid: string): boolean {
	try {
		return localStorage.getItem(githubKey(pid)) === "1"
	} catch {
		return false
	}
}
export function saveHideGithub(pid: string, hide: boolean) {
	try {
		if (hide) localStorage.setItem(githubKey(pid), "1")
		else localStorage.removeItem(githubKey(pid))
	} catch {
		/* private mode */
	}
}

const initial: State = {
	me: null,
	meError: null,
	projectId: null,
	role: null,
	agents: {},
	people: [],
	rooms: {},
	messages: {},
	sending: {},
	threadsLoaded: {},
	metrics: null,
	loading: false,
	loadError: null,
	hasOlder: true,
	sockets: {},
	pulse: 0,
	activity: {},
	hideGithub: false,
	now: Date.now(),
	toasts: [],
	ui: {
		roomId: null,
		threadId: null,
		selectedId: null,
		leftOpen: prefs.leftOpen ?? true,
		rightOpen: prefs.rightOpen ?? true,
		palette: false,
		shortcuts: false,
		connect: false,
		settings: false,
		newProject: false,
		userMenu: false,
		filter: "all",
		authorFilter: null,
		focusAgentId: null,
		resolveFor: null,
		withdrawFor: null,
		citeAdd: null,
		focusComposer: { target: "main", tick: 0 },
	},
}

let state: State = initial
const listeners = new Set<() => void>()

export const getState = () => state

export function setState(patch: Partial<State> | ((s: State) => Partial<State>)) {
	const p = typeof patch === "function" ? patch(state) : patch
	state = { ...state, ...p }
	for (const l of listeners) l()
}

export function setUi(patch: Partial<UiState> | ((u: UiState) => Partial<UiState>)) {
	setState(s => ({
		ui: { ...s.ui, ...(typeof patch === "function" ? patch(s.ui) : patch) },
	}))
}

const subscribe = (l: () => void) => {
	listeners.add(l)
	return () => listeners.delete(l)
}

/** Selectors must return stable references (a slice of state, or a primitive). */
export function useStore<T>(sel: (s: State) => T): T {
	return useSyncExternalStore(
		subscribe,
		() => sel(state),
		() => sel(state),
	)
}

// Re-evaluate time-based state (delivery failures, ages) every 10s.
setInterval(() => setState({ now: Date.now() }), 10_000)
